import React, { useState, useEffect } from 'react';
import { getExchangeRates } from '../services/currencyApi';

// Top 16 currencies to show by default (with flags)
const TOP_CURRENCIES = [
  { code: 'SGD', flag: '\ud83c\uddf8\ud83c\uddec' },
  { code: 'USD', flag: '\ud83c\uddfa\ud83c\uddf8' },
  { code: 'EUR', flag: '\ud83c\uddea\ud83c\uddfa' },
  { code: 'GBP', flag: '\ud83c\uddec\ud83c\udde7' },
  { code: 'JPY', flag: '\ud83c\uddef\ud83c\uddf5' },
  { code: 'CNY', flag: '\ud83c\udde8\ud83c\uddf3' },
  { code: 'HKD', flag: '\ud83c\udded\ud83c\uddf0' },
  { code: 'AUD', flag: '\ud83c\udde6\ud83c\uddfa' },
  { code: 'CAD', flag: '\ud83c\udde8\ud83c\udde6' },
  { code: 'IDR', flag: '\ud83c\uddee\ud83c\udde9' },
  { code: 'MYR', flag: '\ud83c\uddf2\ud83c\uddfe' },
  { code: 'THB', flag: '\ud83c\uddf9\ud83c\udded' },
  { code: 'VND', flag: '\ud83c\uddfb\ud83c\uddf3' },
  { code: 'PHP', flag: '\ud83c\uddf5\ud83c\udded' },
  { code: 'KRW', flag: '\ud83c\uddf0\ud83c\uddf7' },
  { code: 'INR', flag: '\ud83c\uddee\ud83c\uddf3' }
];

const TOP_CURRENCY_CODES = TOP_CURRENCIES.map(c => c.code);
const FLAG_MAP = TOP_CURRENCIES.reduce((acc, c) => ({ ...acc, [c.code]: c.flag }), {});

function CurrencyRatesDashboard() {
  const [baseCurrency, setBaseCurrency] = useState('SGD');
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('custom');
  const [sortOrder, setSortOrder] = useState('asc');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [source, setSource] = useState('');

  useEffect(() => {
    fetchRates();
  }, [baseCurrency]);

  const fetchRates = async (refresh = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getExchangeRates(baseCurrency, refresh);
      console.log('📊 [CurrencyRatesDashboard] getExchangeRates response:', response);

      const data = response.data;

      if (!data) {
        throw new Error('No data in response');
      }

      const ratesData = data.rates || [];
      if (!Array.isArray(ratesData)) {
        console.warn('⚠️ [CurrencyRatesDashboard] data.rates is not an array:', ratesData);
        setRates([]);
      } else {
        console.log('✅ [CurrencyRatesDashboard] Valid rates array with', ratesData.length, 'items');
        setRates(ratesData);
      }

      setLastUpdated(data.fetchedAt);
      setSource(data.source);
    } catch (err) {
      setError(err.message);
      console.error('❌ [CurrencyRatesDashboard] Failed to fetch exchange rates:', err);
    } finally {
      setLoading(false);
    }
  };

  // When searching: show ALL matching currencies
  // When not searching: show only TOP 16 currencies
  const filteredRates = searchQuery.trim()
    ? rates.filter(rate =>
        rate.code.includes(searchQuery.toUpperCase()) ||
        rate.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : rates.filter(rate => TOP_CURRENCY_CODES.includes(rate.code));

  const sortedRates = [...filteredRates].sort((a, b) => {
    // Default: sort by custom order (TOP_CURRENCIES order)
    if (sortBy === 'custom') {
      const aIdx = TOP_CURRENCY_CODES.indexOf(a.code);
      const bIdx = TOP_CURRENCY_CODES.indexOf(b.code);
      // Top currencies first in defined order, others alphabetically at end
      if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
      if (aIdx >= 0) return -1;
      if (bIdx >= 0) return 1;
      return a.code.localeCompare(b.code);
    }

    let aVal, bVal;

    switch (sortBy) {
      case 'code':
        aVal = a.code;
        bVal = b.code;
        break;
      case 'rate':
        aVal = parseFloat(a.rate);
        bVal = parseFloat(b.rate);
        break;
      case 'name':
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      default:
        aVal = a.code;
        bVal = b.code;
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      backgroundColor: 'var(--color-bg-base)'
    },
    header: {
      marginBottom: '24px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      letterSpacing: '-0.025em',
      color: 'var(--color-text-primary)',
      marginBottom: '16px'
    },
    controls: {
      display: 'flex',
      gap: '12px',
      marginBottom: '24px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    select: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid var(--color-border-light)',
      fontSize: '14px',
      fontFamily: 'inherit',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)'
    },
    input: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid var(--color-border-light)',
      fontSize: '14px',
      fontFamily: 'inherit',
      flex: 1,
      minWidth: '200px'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '14px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '600',
      backgroundColor: 'var(--color-primary)',
      color: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    infoBar: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px',
      padding: '12px',
      backgroundColor: 'var(--color-bg-light)',
      borderRadius: '10px',
      flexWrap: 'wrap'
    },
    infoItem: {
      fontSize: '13px',
      color: 'var(--color-text-secondary)'
    },
    badge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: 'var(--color-primary)',
      color: 'white'
    },
    tableWrapper: {
      overflowX: 'auto',
      borderRadius: '10px',
      border: '1px solid var(--color-border-light)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '14px'
    },
    thead: {
      backgroundColor: 'var(--color-bg-light)',
      borderBottom: '2px solid var(--color-border-light)'
    },
    th: {
      padding: '12px',
      textAlign: 'left',
      fontWeight: '600',
      color: 'var(--color-text-primary)',
      cursor: 'pointer',
      userSelect: 'none'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid var(--color-border-light)',
      color: 'var(--color-text-secondary)'
    },
    tdCode: {
      fontWeight: '600',
      color: 'var(--color-primary)',
      fontFamily: 'monospace'
    },
    tdRate: {
      fontFamily: 'monospace',
      textAlign: 'right',
      color: 'var(--color-text-primary)'
    },
    loadingContainer: {
      padding: '40px',
      textAlign: 'center',
      color: 'var(--color-text-secondary)'
    },
    errorContainer: {
      padding: '16px',
      backgroundColor: '#fee',
      border: '1px solid #fcc',
      borderRadius: '10px',
      color: '#c33',
      marginBottom: '24px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>💱 Currency Exchange Rates</div>
      </div>

      {error && (
        <div style={styles.errorContainer}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={styles.controls}>
        <label style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
          Base Currency:
        </label>
        <select
          value={baseCurrency}
          onChange={(e) => setBaseCurrency(e.target.value)}
          style={styles.select}
        >
          <option value="SGD">{'\ud83c\uddf8\ud83c\uddec'} SGD - Singapore Dollar</option>
          <option value="USD">{'\ud83c\uddfa\ud83c\uddf8'} USD - US Dollar</option>
          <option value="EUR">{'\ud83c\uddea\ud83c\uddfa'} EUR - Euro</option>
          <option value="GBP">{'\ud83c\uddec\ud83c\udde7'} GBP - British Pound</option>
          <option value="JPY">{'\ud83c\uddef\ud83c\uddf5'} JPY - Japanese Yen</option>
          <option value="CNY">{'\ud83c\udde8\ud83c\uddf3'} CNY - Chinese Yuan</option>
          <option value="HKD">{'\ud83c\udded\ud83c\uddf0'} HKD - Hong Kong Dollar</option>
          <option value="AUD">{'\ud83c\udde6\ud83c\uddfa'} AUD - Australian Dollar</option>
          <option value="CAD">{'\ud83c\udde8\ud83c\udde6'} CAD - Canadian Dollar</option>
          <option value="MYR">{'\ud83c\uddf2\ud83c\uddfe'} MYR - Malaysian Ringgit</option>
          <option value="IDR">{'\ud83c\uddee\ud83c\udde9'} IDR - Indonesian Rupiah</option>
          <option value="THB">{'\ud83c\uddf9\ud83c\udded'} THB - Thai Baht</option>
          <option value="VND">{'\ud83c\uddfb\ud83c\uddf3'} VND - Vietnamese Dong</option>
          <option value="PHP">{'\ud83c\uddf5\ud83c\udded'} PHP - Philippine Peso</option>
          <option value="KRW">{'\ud83c\uddf0\ud83c\uddf7'} KRW - South Korean Won</option>
          <option value="INR">{'\ud83c\uddee\ud83c\uddf3'} INR - Indian Rupee</option>
        </select>

        <input
          type="text"
          placeholder="Search all currencies (e.g., CHF, peso, won)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.input}
        />

        <button
          onClick={() => fetchRates(true)}
          disabled={loading}
          style={{
            ...styles.button,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      <div style={styles.infoBar}>
        <div style={styles.infoItem}>
          <strong>Showing:</strong> {sortedRates.length} {searchQuery.trim() ? 'matching' : 'top'} currencies
          {!searchQuery.trim() && <span style={{ color: 'var(--color-text-tertiary)', marginLeft: '4px' }}>(search to see all {rates.length})</span>}
        </div>
        <div style={styles.infoItem}>
          <strong>Last Updated:</strong> {lastUpdated ? formatDate(lastUpdated) : 'Never'}
        </div>
        <div style={styles.infoItem}>
          <strong>Source:</strong> <span style={styles.badge}>{source || 'unknown'}</span>
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingContainer}>Loading exchange rates...</div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th} onClick={() => {
                  setSortBy('code');
                  setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                }}>
                  Code {sortBy === 'code' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th style={styles.th}>
                  Name
                </th>
                <th style={styles.th} onClick={() => {
                  setSortBy('rate');
                  setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                }}>
                  Rate {sortBy === 'rate' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th style={styles.th}>
                  Symbol
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRates.map((rate) => (
                <tr key={rate.code}>
                  <td style={{ ...styles.td, ...styles.tdCode }}>
                    {FLAG_MAP[rate.code] && <span style={{ marginRight: '6px' }}>{FLAG_MAP[rate.code]}</span>}
                    {rate.code}
                  </td>
                  <td style={styles.td}>
                    {rate.name}
                  </td>
                  <td style={{ ...styles.td, ...styles.tdRate }}>
                    {parseFloat(rate.rate).toFixed(4)}
                  </td>
                  <td style={styles.td}>
                    {rate.symbol}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CurrencyRatesDashboard;
